import { useState, useEffect, useRef } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { TrendingUp, TrendingDown, ChevronRight, ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useProfitLoss } from '@/hooks/use-reports';
import type { ReportSection } from '@/types';
import { ExportButton } from '@/components/reports/export-button';
import { formatCurrency } from '@/lib/utils';

export const Route = createFileRoute('/reports/profit-loss')({
  component: ProfitLossPage,
});



function formatPercent(value: number) {
  return `${(value * 100).toFixed(2)}%`;
}

function ReportLineRow({ 
  line, 
  depth = 0 
}: { 
  line: any; 
  depth?: number;
}) {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = line.children && line.children.length > 0;
  
  return (
    <>
      <div
        className={`flex justify-between py-0.5 text-sm ${line.isBold || line.isHeader ? 'font-bold' : ''} ${hasChildren ? 'cursor-pointer hover:bg-muted/50 rounded' : ''}`}
        style={{ paddingLeft: `${depth * 16}px` }}
        onClick={() => hasChildren && setIsExpanded(!isExpanded)}
      >
        <span className="flex items-center gap-1">
          {hasChildren ? (
            isExpanded ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />
          ) : (
            <span className="w-4 shrink-0" />
          )}
          {line.accountCode && !line.isHeader && (
            <span className="font-mono text-xs text-muted-foreground w-16 shrink-0">
              {line.accountCode}
            </span>
          )}
          <span>{line.accountName}</span>
        </span>
        <span className="font-mono text-right">{formatCurrency(line.amount)}</span>
      </div>
      
      {hasChildren && isExpanded && line.children.map((child: any, i: number) => (
        <ReportLineRow key={i} line={child} depth={depth + 1} />
      ))}

      {(line.isHeader || hasChildren) && isExpanded && (
        <div 
          className="flex justify-between py-0.5 text-sm font-bold border-t border-black/10 mt-0.5 mb-1"
          style={{ paddingLeft: `${depth * 16 + 20}px` }}
        >
          <span className="uppercase">Jumlah {line.accountName}</span>
          <span className="font-mono">{formatCurrency(line.amount)}</span>
        </div>
      )}
    </>
  );
}

function HierarchicalSectionDisplay({ 
  section,
  isRoot = true,
}: { 
  section: ReportSection;
  isRoot?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div className={`space-y-1 ${isRoot ? 'mb-6' : 'mt-4'}`}>
      <h3 
        className={`font-bold text-sm uppercase flex items-center gap-1 cursor-pointer hover:text-primary ${isRoot ? 'border-b-2 border-black pb-1 mb-2' : ''}`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
        {section.title}
      </h3>
      
      {isExpanded && (
        <div className="space-y-0 pl-1">
          {section.lines && section.lines.length > 0 ? (
            section.lines.map((line, i) => (
              <ReportLineRow key={i} line={line} depth={0} />
            ))
          ) : !section.subSections ? (
            <div className="text-sm text-muted-foreground py-1 italic pl-5">Tidak ada data</div>
          ) : null}

          {section.subSections && section.subSections.map((sub, i) => (
            <HierarchicalSectionDisplay key={i} section={sub} isRoot={false} />
          ))}
        </div>
      )}

      {isRoot && (
        <div className="flex justify-between font-bold border-t-2 border-black py-1 mt-2 text-sm bg-muted/5 px-1">
          <span className="uppercase">TOTAL {section.title}</span>
          <span className="font-mono">{formatCurrency(section.total)}</span>
        </div>
      )}
    </div>
  );
}

function ProfitLossPage() {
  const defaultStart = format(startOfMonth(new Date()), 'yyyy-MM-dd');
  const defaultEnd = format(endOfMonth(new Date()), 'yyyy-MM-dd');

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const reportRef = useRef<HTMLDivElement>(null);

  const { data: report, isLoading, error } = useProfitLoss(startDate, endDate);

  useEffect(() => {
    if (report && reportRef.current) {
      setTimeout(() => {
        reportRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [report]);

  return (
    <div className="flex flex-col gap-4 w-full overflow-y-auto pb-10">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold tracking-tight">Laba Rugi (Profit & Loss)</h1>
        <ExportButton 
          reportType="profit-loss" 
          params={{ startDate, endDate }}
        />
      </div>

      {/* Date Range */}
      <Card className="w-fit">
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="space-y-1">
              <Label className="text-xs font-medium">Dari Tanggal</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-40"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium">Sampai Tanggal</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-40"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : error ? (
        <div className="text-center py-8 text-destructive">
          <p>Gagal memuat laporan laba rugi.</p>
        </div>
      ) : report ? (
        <Card ref={reportRef} className="overflow-hidden">
          <CardHeader className="text-center pb-4 border-b">
            <CardTitle className="text-lg font-bold">LAUNDRY POS</CardTitle>
            <div className="text-sm font-medium">LAPORAN LABA RUGI (PROFIT & LOSS)</div>
            <div className="text-xs text-muted-foreground">
              Periode {format(new Date(report.startDate), 'dd MMMM yyyy')} s/d {format(new Date(report.endDate), 'dd MMMM yyyy')}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {/* Standard Indonesian P&L Layout - Single Column */}
            <div className="p-4 space-y-4">
              {report.sections && report.sections.length > 0 ? (
                // New Hierarchical Tree Rendering
                <>
                  {report.sections.map((section, idx) => (
                    <HierarchicalSectionDisplay key={idx} section={section} />
                  ))}
                </>
              ) : (
                // Legacy Flat Rendering Fallback
                <>
                  {/* Revenue */}
                  <HierarchicalSectionDisplay section={report.revenue} />

                  {/* COGS */}
                  <HierarchicalSectionDisplay section={report.cogs} />

                  {/* Gross Profit */}
                  <div className="flex justify-between items-center font-bold border-t-2 border-black py-2 text-sm bg-muted/20">
                    <span className="uppercase">LABA KOTOR (Gross Profit)</span>
                    <span className="flex items-center gap-3">
                      <span className="font-mono">{formatCurrency(report.grossProfit.amount)}</span>
                      <span className="text-xs text-muted-foreground font-normal">
                        ({formatPercent(report.grossProfit.margin)})
                      </span>
                    </span>
                  </div>

                  {/* Operating Expenses */}
                  <HierarchicalSectionDisplay section={report.operatingExpenses} />

                  {/* Operating Profit */}
                  <div className="flex justify-between items-center font-bold border-t-2 border-black py-2 text-sm bg-muted/20">
                    <span className="uppercase">LABA OPERASIONAL (Operating Profit)</span>
                    <span className="flex items-center gap-3">
                      <span className="font-mono">{formatCurrency(report.operatingProfit.amount)}</span>
                      <span className="text-xs text-muted-foreground font-normal">
                        ({formatPercent(report.operatingProfit.margin)})
                      </span>
                    </span>
                  </div>

                  {/* Other Income */}
                  {report.otherIncome && report.otherIncome.lines.length > 0 && (
                    <HierarchicalSectionDisplay section={report.otherIncome} />
                  )}

                  {/* Other Expenses */}
                  {report.otherExpenses && report.otherExpenses.lines.length > 0 && (
                    <HierarchicalSectionDisplay section={report.otherExpenses} />
                  )}
                </>
              )}
            </div>

            {/* Net Profit - Highlighted */}
            <div className="border-t-2 border-black p-4 bg-muted/30">
              <div className="flex justify-between items-center font-bold text-base">
                <span className="flex items-center gap-2 uppercase">
                  {report.netProfit.amount >= 0 ? (
                    <TrendingUp className="h-5 w-5 text-green-600" />
                  ) : (
                    <TrendingDown className="h-5 w-5 text-red-600" />
                  )}
                  LABA BERSIH (Net Profit)
                </span>
                <span className={`font-mono text-lg ${report.netProfit.amount >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {formatCurrency(report.netProfit.amount)}
                  <span className="text-sm ml-2 text-muted-foreground font-normal">
                    ({formatPercent(report.netProfit.margin)})
                  </span>
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
